import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Plus, Trash2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { saveSettings, getDefaultPrompts } from '../utils/storage';
import { toast } from 'sonner';
import { useTheme } from '../components/ThemeProvider';

export const Onboarding = ({ onComplete }) => {
  const { theme } = useTheme();
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [goals, setGoals] = useState([{ text: '', easyText: '' }]);

  const handleAddGoal = () => {
    if (goals.length >= 5) {
      toast.error('Maximum 5 goals allowed');
      return;
    }
    setGoals([...goals, { text: '', easyText: '' }]);
  };

  const handleRemoveGoal = (index) => {
    setGoals(goals.filter((_, i) => i !== index));
  };

  const handleGoalChange = (index, field, value) => {
    const updated = [...goals];
    updated[index] = { ...updated[index], [field]: value };
    setGoals(updated);
  };

  const handleNameContinue = () => {
    if (!name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    setStep(2);
  };

  const handleComplete = () => {
    // Goals are optional — filter valid ones (must have text)
    const validGoals = goals.filter(g => g.text.trim()).map((g, i) => ({
      id: `goal_${i + 1}`,
      text: g.text.trim(),
      easyText: g.easyText.trim(),
      enabled: true,
      order: i,
    }));

    saveSettings({
      name: name.trim(),
      goals: validGoals,
      prompts: getDefaultPrompts(),
      migrated: true,
    });

    if (onComplete) onComplete();
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      alignItems: 'center',
      minHeight: '100dvh',
      padding: '20px',
      paddingTop: step === 1 ? 'clamp(48px, 10vh, 88px)' : 'clamp(32px, 7vh, 64px)',
      textAlign: 'center',
      backgroundColor: 'var(--theme-light, #0E1A2A)',
      color: 'var(--theme-text-primary, #EAF4FF)'
    }}>
      <div style={{ width: '100%', maxWidth: '400px', margin: '0 auto', paddingBottom: step === 1 ? '36px' : '56px' }}>
        <AnimatePresence mode="wait">
          {step === 1 ? (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
                <h1 className="type-page-title mb-3 text-center">
                Welcome to<br />RE-BUILD-ME
              </h1>
              <p className="font-body text-secondary text-sm text-center">
                Your private daily reset app. No accounts, no tracking, just you.
              </p>

              <label className="block font-body text-secondary mb-2 text-sm mt-8">
                What should we call you?
              </label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your name"
                className="bg-theme-light backdrop-blur border-theme-secondary rounded-2xl p-4 text-sm font-body"
                data-testid="onboarding-name-input"
                autoFocus
              />

              <div className="mt-8">
                <Button
                  onClick={handleNameContinue}
                  style={{
                    width: '100%',
                    backgroundColor: 'var(--theme-btn-bg, #2F5C8F)',
                    color: 'var(--theme-text-primary, #EAF4FF)',
                    borderRadius: '9999px',
                    padding: '20px 16px',
                    fontFamily: 'var(--font-body)',
                    fontWeight: '500',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '12px',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--theme-btn-hover, #1C3552)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-btn-bg, #2F5C8F)'}
                  data-testid="onboarding-continue-btn"
                >
                  Continue
                  <ArrowRight size={20} />
                </Button>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
                <h1 className="type-page-title mb-3 text-center">
                Set your goals
              </h1>
              <p className="font-body text-secondary text-sm text-center">
                Add up to 5 goals you want to rebuild. Goals are optional — you can skip and add later in Settings.
              </p>

              <p className="font-body text-xs text-secondary text-center mt-2" data-testid="goals-count">
                {goals.length}/5 goals added
              </p>

              <div className="space-y-4 mt-6 max-h-[50vh] overflow-y-auto px-1">
                {goals.map((goal, index) => (
                  <div key={index} className="bg-theme-light backdrop-blur rounded-3xl p-5 shadow-ambient border border-theme-secondary">
                    <div className="flex items-center justify-between mb-4">
                      <span className="font-body font-medium" style={{ color: 'var(--theme-text-primary, #EAF4FF)' }}>
                        Goal {index + 1}
                      </span>
                      {goals.length > 0 && (
                        <button
                          onClick={() => handleRemoveGoal(index)}
                          className="p-2 rounded-full transition-colors"
                          style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
                          data-testid={`remove-goal-${index}`}
                        >
                          <Trash2 size={18} style={{ color: 'var(--theme-accent, #68B5FF)' }} />
                        </button>
                      )}
                    </div>

                    <div className="space-y-3">
                      <div>
                        <label className="block font-body text-xs mb-1" style={{ color: 'var(--theme-text-secondary, #B5C9DF)' }}>
                          Your goal
                        </label>
                        <Input
                          value={goal.text}
                          onChange={(e) => handleGoalChange(index, 'text', e.target.value)}
                          placeholder="eg. Go for a 20 minute walk"
                          className="border-theme-secondary rounded-xl text-sm"
                          style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            color: 'var(--theme-text-primary, #EAF4FF)'
                          }}
                          data-testid={`goal-${index}-text`}
                        />
                      </div>

                      <div>
                        <label className="block font-body text-xs mb-1" style={{ color: 'var(--theme-text-secondary, #B5C9DF)' }}>
                          Your easy goal (optional)
                        </label>
                        <Input
                          value={goal.easyText}
                          onChange={(e) => handleGoalChange(index, 'easyText', e.target.value)}
                          placeholder="eg. Sit in the Garden"
                          className="border-theme-secondary rounded-xl text-sm"
                          style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            color: 'var(--theme-text-primary, #EAF4FF)'
                          }}
                          data-testid={`goal-${index}-easy`}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {goals.length < 5 && (
                <button
                  onClick={handleAddGoal}
                  style={{
                    width: '100%',
                    padding: '16px',
                    marginTop: '16px',
                    borderRadius: '16px',
                    border: `2px dashed var(--theme-accent, #68B5FF)`,
                    backgroundColor: 'transparent',
                    color: 'var(--theme-accent, #68B5FF)',
                    fontFamily: 'var(--font-body)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(104, 181, 255, 0.1)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                  data-testid="add-goal-btn"
                >
                  <Plus size={20} />
                  Add another goal
                </button>
              )}

              <div className="mt-8">
                <Button
                  onClick={handleComplete}
                  style={{
                    width: '100%',
                    backgroundColor: 'var(--theme-btn-bg, #2F5C8F)',
                    color: 'var(--theme-text-primary, #EAF4FF)',
                    borderRadius: '9999px',
                    padding: '20px 16px',
                    fontFamily: 'var(--font-body)',
                    fontWeight: '500',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '12px',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--theme-btn-hover, #1C3552)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--theme-btn-bg, #2F5C8F)'}
                  data-testid="onboarding-complete-btn"
                >
                  Start Rebuilding
                  <ArrowRight size={20} />
                </Button>
                <p className="text-center font-body text-xs text-secondary mt-3">
                  You can always change these in Settings
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Onboarding;